
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect, useRef, useState } from 'react';

const TestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const testimonials = [
    {
      name: 'Mehmet Yılmaz',
      company: 'Yılmaz Sigorta Acentesi',
      role: 'Acente Sahibi',
      text: 'Sigorota360 ile çalışmaya başladıktan sonra dijital varlığımız inanılmaz güçlendi. Sosyal medya stratejileri ve reklam kampanyalarıyla müşteri sayımız 3 ayda %150 arttı.',
      image: 'https://public.readdy.ai/ai/img_res/8a1ad3f7da401fe23e8d28293fe0f0b7.jpg',
      rating: 5,
    },
    {
      name: 'Ayşe Demir',
      company: 'Demir Sigorta',
      role: 'Genel Müdür',
      text: 'Sosyal medya yönetimi ve reklam kampanyalarıyla müşteri portföyümüzü kısa sürede genişlettik. CRM entegrasyonu sayesinde müşteri takibi çok kolaylaştı.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20businesswoman%20portrait%2C%20female%20insurance%20agent%20in%20elegant%20navy%20blue%20business%20attire%2C%20warm%20smile%2C%20corporate%20headshot%2C%20clean%20white%20background%2C%20professional%20photography%2C%20confident%20appearance%2C%20executive%20style%2C%20high%20quality&width=300&height=300&seq=testimonial-002&orientation=squarish',
      rating: 5,
    },
    {
      name: 'Can Öztürk',
      company: 'Öztürk Sigorta Danışmanlık',
      role: 'Kurucu Ortak',
      text: 'Web sitemiz ve CRM entegrasyonu sayesinde müşteri takibimiz çok kolaylaştı. Otomatik lead yönetimi ile hiçbir fırsatı kaçırmıyoruz.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20male%20insurance%20consultant%20portrait%2C%20young%20businessman%20in%20modern%20navy%20blue%20suit%2C%20friendly%20smile%2C%20corporate%20headshot%2C%20clean%20white%20background%2C%20professional%20photography%2C%20approachable%20appearance%2C%20contemporary%20business%20style%2C%20high%20quality&width=300&height=300&seq=testimonial-003&orientation=squarish',
      rating: 5,
    },
    {
      name: 'Zeynep Kaya',
      company: 'Kaya Sigorta',
      role: 'Pazarlama Müdürü',
      text: 'Hedef kitle segmentasyonu ve özelleştirilmiş kampanyalar sayesinde lead kalitemiz çok arttı. ROI oranlarımız beklentilerimizin çok üzerinde.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20female%20marketing%20manager%20portrait%2C%20businesswoman%20in%20elegant%20navy%20blue%20blazer%2C%20confident%20smile%2C%20corporate%20headshot%2C%20clean%20white%20background%2C%20professional%20photography%2C%20modern%20appearance%2C%20executive%20style%2C%20high%20quality&width=300&height=300&seq=testimonial-004&orientation=squarish',
      rating: 5,
    },
    {
      name: 'Burak Şahin',
      company: 'Şahin Sigorta',
      role: 'Dijital Pazarlama Sorumlusu',
      text: 'Video içerikler ve sosyal medya stratejileriyle marka bilinirliğimiz katlandı. Artık bölgemizde ilk akla gelen acente biziz.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20digital%20marketing%20specialist%20portrait%2C%20young%20male%20professional%20in%20navy%20blue%20suit%2C%20modern%20smile%2C%20corporate%20headshot%2C%20clean%20white%20background%2C%20professional%20photography%2C%20tech-savvy%20appearance%2C%20contemporary%20style%2C%20high%20quality&width=300&height=300&seq=testimonial-005&orientation=squarish',
      rating: 5,
    },
    {
      name: 'Elif Yıldız',
      company: 'Yıldız Sigorta Grubu',
      role: 'Operasyon Müdürü',
      text: 'Otomasyon sistemleri ve dijital süreçler sayesinde operasyonel verimliliğimiz %200 arttı. Ekibimiz artık stratejik işlere odaklanabiliyor.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20female%20operations%20manager%20portrait%2C%20businesswoman%20in%20elegant%20navy%20blue%20business%20suit%2C%20professional%20smile%2C%20corporate%20headshot%2C%20clean%20white%20background%2C%20professional%20photography%2C%20executive%20appearance%2C%20modern%20style%2C%20high%20quality&width=300&height=300&seq=testimonial-006&orientation=squarish',
      rating: 5,
    },
  ];

  // Auto scroll
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isDragging) return;

    const scroll = () => {
      if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
        scrollContainer.scrollLeft = 0;
      } else {
        scrollContainer.scrollLeft += 1;
      }
    };

    const interval = setInterval(scroll, 30);
    return () => clearInterval(interval);
  }, [isDragging]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (scrollRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 2;
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  return (
    <section ref={ref} className="py-16 bg-gradient-to-br from-[#007dff]/5 via-white to-[#007dff]/5 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <p className="font-poppins font-semibold text-[12px] text-[#007dff] uppercase tracking-wider mb-3">
            Referanslar
          </p>
          <h2 className="font-poppins font-semibold text-3xl lg:text-4xl text-gray-900 mb-4">
            Acenteler Ne Diyor?
          </h2>
          <p className="font-poppins text-base text-gray-600 max-w-2xl mx-auto">
            Birlikte çalıştığımız sigorta acentelerinin başarı hikayeleri
          </p>
        </motion.div>

        {/* Testimonials Scroll */}
        <div 
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseMove={handleMouseMove}
          className="flex gap-6 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing pb-4"
          style={{ scrollBehavior: isDragging ? 'auto' : 'smooth' }}
        >
          {[...testimonials, ...testimonials].map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: (index % testimonials.length) * 0.1 }}
              className="flex-shrink-0 w-[380px] bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300"
            >
              {/* Profile */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-4 border-[#007dff]/20 flex-shrink-0">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-poppins font-bold text-[15px] text-gray-900 mb-1 truncate">
                    {testimonial.name}
                  </h4>
                  <p className="font-poppins text-[12px] text-gray-600 truncate">
                    {testimonial.role}
                  </p>
                  <p className="font-poppins text-[11px] text-[#007dff] font-semibold truncate">
                    {testimonial.company}
                  </p>
                </div>
              </div>

              {/* Stars */}
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm text-yellow-400"></i>
                ))}
              </div>

              {/* Testimonial */}
              <div>
                <i className="ri-double-quotes-l text-2xl text-[#007dff]/20 mb-2 block"></i>
                <p className="font-poppins text-[14px] text-gray-700 leading-relaxed">
                  {testimonial.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Scroll Hint */}
        <div className="text-center mt-6">
          <p className="font-poppins text-[12px] text-gray-500">
            <i className="ri-arrow-left-right-line mr-1"></i>
            Kaydırarak daha fazla yorum görün
          </p>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
